import { MATCH_RESULTS } from 'constants/play';
import type { MatchState, PlayViewer, RoundState, TournamentState } from 'types/interfaces/play';

/** The viewer's side of their match, with games oriented mine-first. */
export interface MyMatchView {
  match: MatchState;
  isBye: boolean;
  /** The viewer's own seat */
  me: { username: string; record: string };
  opponent: { username: string; record: string } | null;
  tableNumber: number | null;
  /** Reported games, mine first; null until a result exists */
  games: [number, number] | null;
  disputed: boolean;
  judgeOpen: boolean;
}

export const myMatchView = (match: MatchState | null | undefined, viewer: PlayViewer): MyMatchView | null => {
  if (!match || !viewer.tournamentPlayerId) return null;
  const iAmA = match.playerA.tournamentPlayerId === viewer.tournamentPlayerId;
  const me = iAmA ? match.playerA : (match.playerB as NonNullable<MatchState['playerB']>);
  const opp = iAmA ? match.playerB : match.playerA;
  const hasResult = match.gamesA !== null && match.gamesB !== null;
  const games: [number, number] | null = hasResult ? (iAmA ? [match.gamesA as number, match.gamesB as number] : [match.gamesB as number, match.gamesA as number]) : null;
  return {
    match,
    isBye: match.isBye || !opp,
    me: { username: me.username, record: me.record },
    opponent: opp ? { username: opp.username, record: opp.record } : null,
    tableNumber: match.tableNumber,
    games,
    disputed: match.status === 'DISPUTED',
    judgeOpen: match.openJudgeCall !== null,
  };
};

export const resultSentence = (games: [number, number]): string => {
  const [mine, opp] = games;
  if (mine > opp) return `You won ${mine}–${opp}`;
  if (mine < opp) return `You lost ${mine}–${opp}`;
  return `Draw ${mine}–${opp}`;
};

/** Elimination rounds (single elim, top cut) do not offer 1-1 (Decision 8). */
export const isEliminationRound = (state: TournamentState, round: RoundState | null): boolean =>
  state.tournament.structure === 'SINGLE_ELIM' || round?.phase === 'TOP_CUT';

/** Best-of-3 games cap on the report steppers (Asana 1220065638706450, Decision 6). */
export const MAX_GAMES = 2;

/**
 * A (mine, opp) pair the report screen may submit: exactly MATCH_RESULTS, minus
 * 1-1 in elimination rounds (Asana 1220065638706450, Decision 7).
 */
export const isReportableResult = (mine: number, opp: number, elimination: boolean): boolean =>
  MATCH_RESULTS.some(([a, b]) => a === mine && b === opp) && !(elimination && mine === opp);

/**
 * Stepper bounds (Decision 6): a value runs 0 to 2; the plus is blocked at 2,
 * and at 1 when the other player already has 2, so 2-2 is unreachable and 2-1
 * is the trailing player's maximum.
 */
export const canIncrementGames = (value: number, other: number): boolean => value < MAX_GAMES && !(other === MAX_GAMES && value === MAX_GAMES - 1);

export const canDecrementGames = (value: number): boolean => value > 0;

export const viewerStanding = (state: TournamentState) => state.standings.find((r) => r.isViewer) ?? null;
